import React, { useState, useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import {
  Box,
  Button,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Grid,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  CardActions,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Divider,
  useTheme,
  Avatar,
  Select,
  FormControl,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Storage as StorageIcon, // Live
  TableChart as TableChartIcon, // Materialized
  PlayArrow as PlayArrowIcon,
  History as HistoryIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as ContentCopyIcon,
  Share as ShareIcon,
  FolderOpen as FolderOpenIcon,
  CloudUpload as CloudUploadIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  CalendarMonth as CalendarIcon
} from '@mui/icons-material';

import { useData } from '../hooks/useData';
import { useTimezoneFormatter } from '../hooks/useTimezoneFormatter';
import { useTenantExportBranding } from '../hooks/useTenantExportBranding';
import { apiFetch } from '../api';
import { LoadingSpinner, ConfirmModal, StandardPageLayout } from '../components/Common';
import { useNotification } from '../context/NotificationContext';
import DatasetBuilderDialog from '../components/DatasetBuilder/DatasetBuilderDialog';
import RunHistoryDialog from '../components/DatasetBuilder/RunHistoryDialog';
import ScheduleDialog from '../components/DatasetBuilder/ScheduleDialog';


// --- Styled Components / Helpers ---

const StatusChip = ({ status }) => {
  let color = 'default';
  if (status === 'active') color = 'success';
  if (status === 'archived') color = 'error';
  if (status === 'draft') color = 'default';

  return (
    <Chip
      label={status || 'draft'}
      size="small"
      color={color}
      sx={{
        fontWeight: 700,
        textTransform: 'uppercase',
        fontSize: '0.65rem',
        height: 24,
        borderRadius: 1
      }}
    />
  );
};

// --- Main Page Component ---

const DatasetsPage = ({ token }) => {
  const { formatDate: formatTz } = useTimezoneFormatter();
  const theme = useTheme();
  const { addNotification } = useNotification();
  const tenantBranding = useTenantExportBranding();
  const { data: datasetsData, isLoading, refresh } = useData('/datasets');
  const datasets = datasetsData || [];

  // --- State ---
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, active, draft

  const [builderOpen, setBuilderOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [selectedDataset, setSelectedDataset] = useState(null);
  const [buildingIds, setBuildingIds] = useState({}); // { datasetId: originalRunId }
  const [confirmDelete, setConfirmDelete] = useState(null);

  // --- Menu State for "More Actions" ---
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuTargetDataset, setMenuTargetDataset] = useState(null);



  // --- Header Menu State ---
  const [headerMenuAnchor, setHeaderMenuAnchor] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'

  // --- Auto-Refresh Logic ---
  useEffect(() => {
    const interval = setInterval(() => {
      // Always poll for status updates while on this page
      refresh(true);
    }, 5000);
    return () => clearInterval(interval);
  }, [refresh]);

  // Clean up buildingIds once the backend status catches up
  useEffect(() => {
    const ids = Object.keys(buildingIds);
    if (ids.length > 0 && datasets.length > 0) {
      setBuildingIds(prev => {
        let changed = false;
        const next = { ...prev };

        datasets.forEach(ds => {
          if (next.hasOwnProperty(ds.id)) {
            const entry = next[ds.id];
            const originalRunId = typeof entry === 'object' ? entry.runId : entry;
            const triggerTime = typeof entry === 'object' ? entry.at : 0;
            const now = dayjs().valueOf();

            // If the backend now sees a DIFFERENT run ID, it's definitely the new one
            const isNewRun = ds.latest_run_id && ds.latest_run_id !== originalRunId;

            // If it was null before (first run) and now has an ID
            const isFirstRunStarted = originalRunId === null && ds.latest_run_id;

            // SAFETY: If the backend currently shows it's actually building, we can release the local lock
            // because the backend-driven 'pending'/'running' status will take over the UI.
            const isCurrentlyActiveInBackend = ds.latest_run_status === 'pending' || ds.latest_run_status === 'running';

            // SAFETY 2: 45 second timeout fallback
            const isStale = triggerTime > 0 && (now - triggerTime > 45000);

            if (isNewRun || isFirstRunStarted || isCurrentlyActiveInBackend || isStale) {
              delete next[ds.id];
              changed = true;
            }
          }
        });

        return changed ? next : prev;
      });
    }
  }, [datasets, buildingIds]);

  // --- Derived Data ---
  const filteredDatasets = useMemo(() => {
    return datasets.filter(ds => {
      const matchesSearch = ds.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (ds.description && ds.description.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchesFilter = filterStatus === 'all' || (ds.status || 'draft') === filterStatus;
      return matchesSearch && matchesFilter;
    });
  }, [datasets, searchQuery, filterStatus]);

  // --- Handlers ---

  const handleCreate = () => {
    setSelectedDataset(null);
    setBuilderOpen(true);
  };

  const handleEdit = (dataset) => {
    setSelectedDataset(dataset);
    setBuilderOpen(true);
    handleMenuClose();
  };

  const handleMenuOpen = (event, dataset) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuTargetDataset(dataset);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuTargetDataset(null);
  };

  const isBuilding = (ds) => {
    return buildingIds.hasOwnProperty(ds.id) || ds.latest_run_status === 'pending' || ds.latest_run_status === 'running';
  };

  const handleBuild = async (id, mode) => {
    if (mode === 'live') {
      addNotification("Live datasets do not need building.", "info");
      return;
    }

    const ds = datasets.find(d => d.id === id);
    setBuildingIds(prev => ({
      ...prev,
      [id]: { runId: ds?.latest_run_id || null, at: dayjs().valueOf() }
    }));
    try {
      await apiFetch(`/datasets/${id}/build`, token, { method: 'POST' });
      addNotification("Build triggered successfully", "success");

      // We DON'T clear buildingIds here anymore to avoid the "flashing" flicker.
      // Instead, the useEffect above will clear it once the poll returns with 'pending' status.

      refresh(true);
    } catch (err) {
      addNotification(err.message, "error");
      setBuildingIds(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  };

  const handleCancelBuild = async (id) => {
    try {
      // console.log(`[FE] Requesting cancellation for dataset build: ${id}`);
      const res = await apiFetch(`/datasets/${id}/cancel-latest`, token, { method: 'POST' });
      // console.log(`[FE] Cancellation result:`, res);

      addNotification("Cancellation requested", "info");

      // Cleanup locally immediately
      setBuildingIds(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });

      // Full refresh
      refresh(true);
    } catch (err) {
      console.error(`[FE] Cancellation failed:`, err);
      addNotification(err.message, "error");
    }
  };

  const handleDeleteClick = () => {
    setConfirmDelete(menuTargetDataset);
    handleMenuClose();
  };

  const confirmDeletion = async () => {
    if (!confirmDelete) return;
    try {
      await apiFetch(`/datasets/${confirmDelete.id}`, token, { method: 'DELETE' });
      addNotification("Dataset deleted", "success");
      refresh();
    } catch (err) {
      addNotification(err.message, "error");
    } finally {
      setConfirmDelete(null);
    }
  };

  const handleExportDataset = async (dataset) => {
    handleMenuClose();
    try {
      const data = await apiFetch(`/datasets/${dataset.id}/export`, token, { method: 'POST' });
      
      // Inject tenant branding metadata
      const exportData = {
          ...data,
          _tenant_branding: {
              exported_at: dayjs().toISOString(),
              watermark: tenantBranding.watermark?.text || null
          }
      };

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dataset-${dataset.name.replace(/\s+/g, '_')}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      addNotification(`Export started for "${dataset.name}"`);
    } catch (e) {
      addNotification(`Export failed: ${e.message}`, "error");
    }
  };

  const handleHistoryJS = () => {
    if (menuTargetDataset) {
      setSelectedDataset(menuTargetDataset);
      setHistoryOpen(true);
      handleMenuClose();
    }
  };

  const handleScheduleRequest = (dataset) => {
    setSelectedDataset(dataset);
    setScheduleOpen(true);
    handleMenuClose();
  };


  const handleImportJSON = (file) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const json = JSON.parse(e.target.result);
        const payload = {
          payload: json,
          config: { connection_mappings: {} }
        };
        await apiFetch('/datasets/import-json', token, { method: 'POST', body: JSON.stringify(payload) });
        addNotification("Dataset imported successfully.");
        refresh();
      } catch (err) {
        addNotification(`Import failed: ${err.message}`, 'error');
      }
    };
    reader.readAsText(file);
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    addNotification(label ? `${label} copied` : "Copied to clipboard", "success");
  };

  // Format Date Helper
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return formatTz(dateString);
  };

  // --- Render ---

  // Prepare Layout Props
  const filterControls = (
    <>
      <TextField
        placeholder="Search..."
        variant="outlined"
        size="small"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
        }}
        sx={{ width: 200, bgcolor: 'background.paper' }}
      />
      <FormControl size="small" sx={{ minWidth: 100 }}>
        <Select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          displayEmpty
          inputProps={{ 'aria-label': 'Filter Status' }}
          sx={{ bgcolor: 'background.paper' }}
        >
          <MenuItem value="all">All Status</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="draft">Draft</MenuItem>
        </Select>
      </FormControl>
    </>
  );

  const handleHeaderMenuOpen = (event) => setHeaderMenuAnchor(event.currentTarget);
  const handleHeaderMenuClose = () => setHeaderMenuAnchor(null);

  const headerActions = (
    <>
      <Divider orientation="vertical" flexItem variant="middle" sx={{ mx: 1 }} />
      <input
        type="file"
        id="ds-import-json"
        style={{ display: 'none' }}
        accept=".json"
        onChange={(e) => {
          const file = e.target.files[0];
          if (file) handleImportJSON(file);
          e.target.value = null;
        }}
      />

      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={handleCreate}
        sx={{
          boxShadow: theme.shadows[2],
          whiteSpace: 'nowrap',
          minWidth: 'auto',
          height: 32
        }}
      >
        <Box component="span" sx={{ display: { xs: 'none', md: 'block' } }}>CREATE DATASET</Box>
      </Button>

      <IconButton
        onClick={handleHeaderMenuOpen}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          height: 32, width: 32,
          ml: 1
        }}
      >
        <MoreVertIcon />
      </IconButton>

      <Menu
        anchorEl={headerMenuAnchor}
        open={Boolean(headerMenuAnchor)}
        onClose={handleHeaderMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => { document.getElementById('ds-import-json').click(); handleHeaderMenuClose(); }}>
          <ListItemIcon><CloudUploadIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Import JSON</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { setViewMode(prev => prev === 'grid' ? 'list' : 'grid'); handleHeaderMenuClose(); }}>
          <ListItemIcon>{viewMode === 'grid' ? <ViewListIcon fontSize="small" /> : <ViewModuleIcon fontSize="small" />}</ListItemIcon>
          <ListItemText>{viewMode === 'grid' ? 'List View' : 'Grid View'}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );

  return (
    <StandardPageLayout
      title="Datasets"
      description="Manage your data products."
      filterControls={filterControls}
      headerActions={headerActions}
    >
      {isLoading ? (
        <LoadingSpinner />
      ) : filteredDatasets.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8, opacity: 0.7 }}>
          <FolderOpenIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body1" color="text.secondary">No datasets found.</Typography>
        </Box>
      ) : (
        <Box sx={{ pb: 4 }}>
          {viewMode === 'list' ? (
            <Box sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
              <table className="w-full text-sm text-left">
                <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400 border-b dark:border-gray-600">
                  <tr>
                    <th className="px-6 py-3 font-medium">Dataset Name</th>
                    <th className="px-6 py-3 font-medium">ID</th>
                    <th className="px-6 py-3 font-medium">Status</th>
                    <th className="px-6 py-3 font-medium">Records</th>
                    <th className="px-6 py-3 font-medium">Last Modified</th>
                    <th className="px-6 py-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredDatasets.map((ds) => (
                    <tr key={ds.id} className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                      <td className="px-6 py-4 font-medium text-gray-900 dark:text-white border-r border-transparent flex items-center gap-3">
                        <Avatar
                          variant="rounded"
                          sx={{
                            bgcolor: ds.materialization_mode === 'live' ? 'secondary.light' : 'primary.light',
                            color: ds.materialization_mode === 'live' ? 'secondary.dark' : 'primary.dark',
                            width: 32, height: 32
                          }}
                        >
                          {ds.materialization_mode === 'live' ? <StorageIcon fontSize="small" /> : <TableChartIcon fontSize="small" />}
                        </Avatar>
                        <div>
                          <div className="font-bold">{ds.name}</div>
                          <div className="text-xs text-gray-500">{ds.materialization_mode === 'live' ? 'Live Query' : 'Cached Data'}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-500 dark:text-gray-400 font-mono text-xs">
                        <Tooltip title="Copy ID">
                          <span onClick={() => copyToClipboard(ds.id)} className="cursor-pointer hover:text-primary-500 border-b border-dashed border-gray-400">
                            {ds.id}
                          </span>
                        </Tooltip>
                      </td>
                      <td className="px-6 py-4">
                        <StatusChip status={ds.status} />
                      </td>
                      <td className="px-6 py-4 text-gray-500 dark:text-gray-400">
                        {ds.row_count !== null && ds.row_count !== undefined ? ds.row_count.toLocaleString() : '-'}
                      </td>
                      <td className="px-6 py-4 text-gray-500 dark:text-gray-400">
                        {formatDate(ds.updated_at || ds.created_at)}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EditIcon />}
                            onClick={() => handleEdit(ds)}
                            sx={{ borderColor: 'divider', color: 'text.primary', '&:hover': { borderColor: 'primary.main', color: 'primary.main' } }}
                          >
                            Edit
                          </Button>
                          {ds.materialization_mode !== 'live' && (
                            <Stack direction="row" spacing={0.5}>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={isBuilding(ds) ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                                onClick={() => handleBuild(ds.id, ds.materialization_mode)}
                                disabled={isBuilding(ds)}
                                sx={{
                                  borderColor: 'divider', color: 'success.main', '&:hover': {
                                    backgroundColor: 'primary.main',
                                    color: 'primary.contrastText',
                                    borderColor: 'primary.main'
                                  }
                                }}
                              >
                                {isBuilding(ds) ? 'Building' : 'Build'}
                              </Button>
                              {isBuilding(ds) && (
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleCancelBuild(ds.id)}
                                  sx={{ border: '1px solid', borderColor: 'error.light', borderRadius: 1 }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Stack>
                          )}
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, ds)}
                            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {filteredDatasets.map((ds) => (
                <Grid item xs={12} sm={6} md={4} lg={3} xl={3} key={ds.id}>
                  <Card
                    elevation={0}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: theme.shadows[4],
                        borderColor: 'primary.main'
                      }
                    }}
                  >
                    {/* Header - Compact */}
                    <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ maxWidth: '85%' }}>
                        <Avatar
                          variant="rounded"
                          sx={{
                            bgcolor: 'transparent',
                            color: ds.materialization_mode === 'live' ? 'secondary.dark' : 'primary.dark',
                            width: 48,
                            height: 48,
                            fontSize: '2rem'
                          }}
                        >
                          {ds.materialization_mode === 'live' ? <StorageIcon fontSize="inherit" /> : <TableChartIcon fontSize="inherit" />}
                        </Avatar>
                        <Box sx={{ overflow: 'hidden' }}>
                          <Typography variant="subtitle2" fontWeight="bold" noWrap title={ds.name}>
                            {ds.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            {ds.materialization_mode === 'live' ? 'Live Query' : 'Cached Data'}
                          </Typography>
                        </Box>
                      </Stack>
                      <IconButton size="small" onClick={(e) => handleMenuOpen(e, ds)} sx={{ p: 0.5 }}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    <Divider sx={{ mx: 1.5, opacity: 0.6 }} />

                    {/* Content - Compact */}
                    <CardContent sx={{ pt: 1.5, pb: 1, px: 1.5, flexGrow: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{
                        mb: 1.5,
                        fontSize: '0.8rem',
                        minHeight: 36, // Reduced height
                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'
                      }}>
                        {ds.description || "No description."}
                      </Typography>

                      {/* Metrics Layout - Compact */}
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.5,
                        p: 1,
                        bgcolor: 'action.hover',
                        borderRadius: 1.5
                      }}>
                        {/* Status Pill */}
                        <Box sx={{ pt: 0.25 }}>
                          <StatusChip status={ds.status} />
                        </Box>

                        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(0,0,0,0.1)' }} />

                        {/* Info Block */}
                        <Box>
                          <Stack direction="row" alignItems="center" spacing={1} mb={0.25}>
                            <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ fontSize: '0.7rem' }}>
                              Records:
                            </Typography>
                            <Typography variant="caption" fontWeight="medium" sx={{ fontSize: '0.7rem' }}>
                              {ds.row_count !== null && ds.row_count !== undefined ? ds.row_count.toLocaleString() : '-'}
                            </Typography>
                          </Stack>

                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ fontSize: '0.7rem' }}>
                              Refreshed:
                            </Typography>
                            <Typography variant="caption" fontWeight="medium" color="text.primary" sx={{ fontSize: '0.7rem' }}>
                              {ds.last_build_at ? formatDate(ds.last_build_at) : formatDate(ds.updated_at)}
                            </Typography>
                          </Stack>
                        </Box>
                      </Box>


                    </CardContent>

                    {/* Actions - Compact */}
                    <CardActions sx={{ p: 1.5, pt: 0 }}>
                      {ds.materialization_mode !== 'live' ? (
                        <Box sx={{ width: '100%', display: 'flex', gap: 1 }}>
                          <Button
                            fullWidth
                            size="small"
                            variant="outlined"
                            onClick={() => handleBuild(ds.id, ds.materialization_mode)}
                            disabled={isBuilding(ds)}
                            startIcon={isBuilding(ds) ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon fontSize="small" />}
                            sx={{
                              py: 0.5,
                              fontSize: '0.8rem',
                              borderColor: 'divider',
                              color: 'success.main',
                              boxShadow: 2,
                              bgcolor: 'background.paper',
                              '&:hover': {
                                backgroundColor: 'primary.main',
                                color: 'primary.contrastText',
                                borderColor: 'primary.main',
                                boxShadow: 3
                              }
                            }}
                          >
                            {isBuilding(ds) ? 'Building' : 'Build Now'}
                          </Button>
                          {isBuilding(ds) && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              onClick={() => handleCancelBuild(ds.id)}
                              sx={{ minWidth: '40px', px: 0 }}
                            >
                              Cancel
                            </Button>
                          )}
                        </Box>
                      ) : (
                        <Button
                          fullWidth
                          size="small"
                          variant="outlined"
                          onClick={() => handleEdit(ds)}
                          startIcon={<EditIcon fontSize="small" />}
                          sx={{
                            py: 0.5,
                            fontSize: '0.8rem',
                            borderColor: 'divider',
                            color: 'success.main',
                            boxShadow: 2,
                            bgcolor: 'background.paper',
                            '&:hover': {
                              backgroundColor: 'primary.main',
                              color: 'primary.contrastText',
                              borderColor: 'primary.main',
                              boxShadow: 3
                            }
                          }}
                        >
                          Edit Config
                        </Button>
                      )}
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {/* --- Action Menu --- */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{ elevation: 3, sx: { minWidth: 160, borderRadius: 2 } }}
      >
        <MenuItem onClick={() => handleEdit(menuTargetDataset)}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { copyToClipboard(menuTargetDataset?.id); handleMenuClose(); }}>
          <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Copy ID</ListItemText>
        </MenuItem>
        {menuTargetDataset?.materialization_mode !== 'live' && (
          <MenuItem onClick={handleHistoryJS}>
            <ListItemIcon><HistoryIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Run History</ListItemText>
          </MenuItem>
        )}
        {menuTargetDataset?.materialization_mode !== 'live' && (
          <MenuItem onClick={() => handleScheduleRequest(menuTargetDataset)}>
            <ListItemIcon><CalendarIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Schedule Refresh</ListItemText>
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={() => handleExportDataset(menuTargetDataset)}>
          <ListItemIcon><ShareIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Export</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { copyToClipboard(menuTargetDataset?.id, "Dataset ID"); handleMenuClose(); }}>
          <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Copy ID</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleDeleteClick}>
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* --- Modals (Builder, History, Delete) --- */}
      <DatasetBuilderDialog
        open={builderOpen}
        onClose={() => setBuilderOpen(false)}
        onSuccess={() => { setBuilderOpen(false); refresh(); addNotification("Dataset saved", "success"); }}
        token={token}
        initialData={selectedDataset}
      />
      {selectedDataset && (
        <RunHistoryDialog
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          datasetId={selectedDataset.id}
          token={token}
        />
      )}
      <ScheduleDialog
        open={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
        dataset={selectedDataset}
        token={token}
        onSuccess={() => { refresh(); addNotification("Schedule updated", "success"); }}
      />
      <ConfirmModal
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={confirmDeletion}
        title="Delete Dataset?"
      >
        Are you sure you want to delete <b>{confirmDelete?.name}</b>?
      </ConfirmModal>
    </StandardPageLayout>
  );
};

export default DatasetsPage;